import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { LocalStorageService } from '../../../../core/services/LocalStorageService';
import { TerminalSessionStatuses } from '../enums/TerminalSessionStatuses';
import { ITerminal } from '../interfaces/ITerminal';
import { ITerminalShowCashPrice } from '../interfaces/ITerminalShowCashPrice';
import { TerminalParameterKeys } from '../enums/TerminalParameterKeys';
import { TerminalParameterValueType } from '../enums/TerminalParameterValueType';
import { MandatorParameterKeys } from '../enums/MandatorParameterKeys';
import { ITerminalPriceCategory } from '../interfaces/ITerminalPriceCategory';
import { IIdAndName } from '../../../../core/interfaces/IIdAndName';
import { PermissionService } from '../../../auth/core/services/PermissionService';
import { WebCashPermissionKeys } from '../../../../core/enums/WebCashPermissionKeys';
import { ISessionBootstrap } from '../../../../core/interfaces/ISessionBootstrap';

@Injectable({
  providedIn: 'root'
})
export class TerminalsStore {

  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _permissionService = inject(PermissionService);

  public readonly selectedTerminal = signal<ITerminal>(this._localStorageService.getItem('selected-terminal'));
  public readonly connectedTerminalSN = signal<number>(
    this._localStorageService.getItem('connected-terminal-sn')
  );
  public readonly lastUsedTerminalsIds = signal<number[]>(
    this._localStorageService.getItem('last-used-terminals-ids') || []
  );
  public readonly terminalPausedSession = signal<{ terminalId: number; employeeId: number; sessionId: number; }>(
    this._localStorageService.getItem('terminal-paused-session')
  );
  public readonly terminals = signal<ITerminal[]>([]);
  public readonly terminalShowCashPrices = signal<ITerminalShowCashPrice[]>([]);

  public readonly reservationPriceCategory = computed<ITerminalPriceCategory>(
    () => this.selectedTerminal().priceCategories.find(p => p.flagReservation)
  );
  public readonly changedCinema = signal<IIdAndName>(
    this._localStorageService.getItem('changed-cinema')
  );

  public readonly terminalParametersMap = computed(() => {
    const map: Record<TerminalParameterKeys, string | boolean | number | null> =
      {} as Record<TerminalParameterKeys, string | boolean | number | null>;

    this.selectedTerminal()?.parameters.forEach(parameter => {
      if (parameter.valueType === TerminalParameterValueType.BOOL) {
        map[parameter.key] =parameter.value === '1' || parameter.value === 'true';
      } else if (parameter.valueType === TerminalParameterValueType.PORT) {
        map[parameter.key] = parameter.value === '0' ? null : parameter.value;
      } else {
        map[parameter.key] = parameter.value;
      }
    });

    return map;
  });

  public readonly systemParametersMap = computed(() => {
    const map: Record<string, string | boolean | number | null> = {};

    this.selectedTerminal().systemParameters.forEach(
      parameter =>
        map[parameter.key] = parameter.value === '1' ? true : parameter.value === '0' ? false : parameter.value
    );

    return map;
  });

  public readonly mandatorParametersMap = computed(() => {
    const map: Record<MandatorParameterKeys, string | boolean | number | null> =
      {} as Record<MandatorParameterKeys, string | boolean | number | null>;

    this.selectedTerminal()?.mandatorParameters?.forEach(
      parameter =>
        map[parameter.key] = parameter.value === '1' ? true : parameter.value === '0' ? false : parameter.value
    );

    return map;
  });

  public readonly arePaymentMethodsRestricted = computed<boolean>(
    () => Array.isArray(this.selectedTerminal()?.allowedPaymentMethods)
  );

  public readonly allowedPaymentMethodsMap = computed<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};

    this.selectedTerminal()?.allowedPaymentMethods?.forEach(method => map[method] = true);

    return map;
  });

  // Effective discount range gated by operator permissions, mirroring legacy CXCash
  // DlgRebate.SetMinMaxRebate: Range2 permission takes priority over Range1 and reads its own
  // CSH_DISCOUNT_RANGE_2 config; otherwise Range1 permission reads CSH_DISCOUNT_RANGE_1.
  public readonly fixedDiscountPercentage = computed<{ min: number; max: number } | null>(() => {
    const map = this.mandatorParametersMap();

    if (this._permissionService.hasPermission(WebCashPermissionKeys.WebCxCashGiveDiscountRange2)) {
      return this.parseDiscountRange(map[MandatorParameterKeys.CSH_DISCOUNT_RANGE_2]);
    }

    if (this._permissionService.hasPermission(WebCashPermissionKeys.WebCxCashGiveDiscountRange1)) {
      return this.parseDiscountRange(map[MandatorParameterKeys.CSH_DISCOUNT_RANGE_1]);
    }

    return null;
  });

  // Parses a discount config value into {min,max}. Mirrors legacy atof-based parsing:
  // "min-max" allows min=0 (e.g. "0-100"), max must be > 0; a single value is valid only when > 0.
  private parseDiscountRange(configValue: string | boolean | number | null): { min: number; max: number } | null {
    if (!configValue || typeof configValue !== 'string') {
      return null;
    }

    const value = configValue.trim();
    if (value === '' || value === '0') {
      return null;
    }

    if (value.includes('-')) {
      const rangeParts = value.split('-');
      if (rangeParts.length === 2) {
        const minValue = parseFloat(rangeParts[0].trim());
        const maxValue = parseFloat(rangeParts[1].trim());
        if (!isNaN(minValue) && !isNaN(maxValue) && minValue >= 0 && maxValue > 0 && maxValue >= minValue) {
          return { min: minValue, max: maxValue };
        }
      }
    } else {
      const singleValue = parseFloat(value);
      if (!isNaN(singleValue) && singleValue > 0) {
        return { min: singleValue, max: singleValue };
      }
    }

    return null;
  }

  public constructor() {
    toObservable(
      this.selectedTerminal
    ).subscribe(selectedTerminal => {
      if (selectedTerminal) {
        this._localStorageService.setItem('selected-terminal', selectedTerminal);

        this.lastUsedTerminalsIds.update(lastUsedTerminalsIds => {
          return lastUsedTerminalsIds ? lastUsedTerminalsIds.filter(id => id !== selectedTerminal.id) : [];
        });

        this.lastUsedTerminalsIds.update(lastUsedTerminalsIds => {
          if (lastUsedTerminalsIds) {
            return [
              selectedTerminal.id,
              ...lastUsedTerminalsIds,
            ];
          } else {
            return [selectedTerminal.id];
          }
        });
      } else {
        this._localStorageService.removeItem('selected-terminal');
      }
    });

    toObservable(
      this.lastUsedTerminalsIds
    ).subscribe(lastUsedTerminalsIds => {
      if (lastUsedTerminalsIds) {
        this._localStorageService.setItem('last-used-terminals-ids', lastUsedTerminalsIds);
      }
    });

    toObservable(
      this.terminalPausedSession
    ).subscribe(terminalPausedSession => {
      this._localStorageService.setItem('terminal-paused-session', terminalPausedSession);
    });

    toObservable(
      this.changedCinema
    ).subscribe(changedCinema => {
      this._localStorageService.setItem('changed-cinema', changedCinema);
    });

    toObservable(
      this.connectedTerminalSN
    ).subscribe(connectedTerminalSN => {
      this._localStorageService.setItem('connected-terminal-sn', connectedTerminalSN);
    });
  }

  public applySessionBootstrap(bootstrap: ISessionBootstrap, terminal: ITerminal | null = null): void {
    const mandator = {
      ...(terminal?.mandator ?? { name: '', token: '' }),
      id: bootstrap.mandatorId,
    };
    const workerNumber = bootstrap.workerNumber || terminal?.worker?.number || '';

    if (!terminal) {
      this.selectedTerminal.set({
        id: bootstrap.terminalId,
        name: '',
        number: '',
        serialNumber: 0,
        mandator,
        cinemaId: bootstrap.cinemaId,
        cinema: { id: bootstrap.cinemaId, name: '' },
        type: 0,
        isActive: true,
        isMy: true,
        priceCategories: [],
        parameters: [],
        systemParameters: [],
        mandatorParameters: [],
        flagReservation: false,
        worker: { number: workerNumber },
        currentSession: {
          id: bootstrap.sessionId,
          terminalId: bootstrap.terminalId,
          mandatorId: bootstrap.mandatorId,
          sessionStatus: TerminalSessionStatuses.Opened,
        },
      } as ITerminal);
      return;
    }

    this.selectedTerminal.set({
      ...terminal,
      id: bootstrap.terminalId,
      cinemaId: bootstrap.cinemaId,
      mandator,
      worker: {
        ...terminal.worker,
        number: workerNumber,
      },
      currentSession: {
        ...terminal.currentSession,
        id: bootstrap.sessionId,
        terminalId: bootstrap.terminalId,
        mandatorId: bootstrap.mandatorId,
        sessionStatus: terminal.currentSession?.sessionStatus ?? TerminalSessionStatuses.Opened,
      },
    });
  }

  public isPaymentMethodAllowed(method: string): boolean {
    return !this.arePaymentMethodsRestricted() || this.allowedPaymentMethodsMap()[method] === true;
  }
}
